import { type ReactNode } from 'react'
import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { BlockerStore } from 'shared/ui/Blocker'
import modalStore from 'shared/ui/Modal/store/modalStore.ts'
import { ModalTypeList } from 'shared/ui/Modal/store/types.ts'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IBaseSmartCallFlowData,
  type IFullSmartCallFlowData,
} from 'entities/SmartCall/api/types'
import { SmartCallApi } from 'entities/SmartCall/api/SmartCallApi.ts'
import { SmartCallFlowBuilderStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder'

export class SmartCallEditStore {
  smartCallFlowBuilderStore
  workflow: IFullSmartCallFlowData | null = null
  blockerStore: BlockerStore
  inboxWorkflowEnabled = false
  loading = true

  constructor(public readonly entities: IEntitiesStore, private id: number) {
    this.smartCallFlowBuilderStore = new SmartCallFlowBuilderStore(this.entities, this)
    makeAutoObservable(this)

    this._syncBlockerConfig()
    this.blockerStore = new BlockerStore(this, this._blockerConfig)
  }

  private _blockerConfig: {
    title: ReactNode
    primaryText: string
    primaryAction: (blockerModalId: string) => Promise<void>
    modalType: ModalTypeList
    additionalSecondaryActionText?: string
  } = {
    title: '',
    primaryText: '',
    primaryAction: async () => this.onSaveChanges({ withError: true }),
    modalType: ModalTypeList.WARNING,
  }

  init = async () => {
    try {
      this.smartCallFlowBuilderStore.init()
      const inbox = await this.entities.inboxesStore.getById(this.id, true)
      if (inbox) this.smartCallFlowBuilderStore.setName(inbox.name)
      await this.initWorkflow()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }
  dispose = () => {
    this.smartCallFlowBuilderStore.dispose()
  }

  private _syncBlockerConfig = () => {
    if (this.hasActiveCallFlow) {
      this._blockerConfig.title = (
        <>
          Publish call flow
          <br />
          before closing?
        </>
      )
      this._blockerConfig.primaryText = 'Publish changes'
      this._blockerConfig.primaryAction = () => this.onPublishChanges({ withError: true })
      this._blockerConfig.additionalSecondaryActionText = 'Don’t publish'
    } else {
      this._blockerConfig.title = (
        <>
          Save call flow
          <br />
          before closing?
        </>
      )
      this._blockerConfig.primaryText = 'Save changes'
      this._blockerConfig.primaryAction = () => this.onSaveChanges({ withError: true })
      this._blockerConfig.additionalSecondaryActionText = 'Don’t save'
    }
  }

  get isCreate() {
    return !this.workflow
  }

  get inbox() {
    const inbox = this.entities.inboxesStore.getItem(this.id)
    if (inbox instanceof Inbox) {
      return inbox
    }
    return null
  }

  get params(): IBaseSmartCallFlowData | null {
    return this.smartCallFlowBuilderStore.params
  }

  get hasActiveCallFlow() {
    return this.inboxWorkflowEnabled && this.workflow?.enabled
  }

  get isInvalid() {
    return this.workflow?.is_invalid ?? false
  }

  get isSaveButtonDisabled() {
    if (this.isCreate) return false

    return !this.blockerStore.isExistChanges
  }

  onActivateInboxWorkflow = async (status: boolean) => {
    if (this.workflow) {
      if (this.hasActiveCallFlow) {
        await this._onShowDisableFlowModal()

        return
      }
      if (!this._checkValidate('activate')) {
        return
      }

      await this.updateInboxWorkflow({ id: this.workflow.id, status })
    } else {
      const prevState = this.inboxWorkflowEnabled
      this.inboxWorkflowEnabled = status
      if (status) {
        this.smartCallFlowBuilderStore.buildDefaultWorkflow()
      }
      await this.onActivateWorkFlow()
      if (!this.workflow) {
        this.inboxWorkflowEnabled = prevState
      }
      this._syncBlockerConfig()
    }
  }

  update = (data: IFullSmartCallFlowData) => {
    this.blockerStore.updateCache()
    this.workflow = data
    this._syncBlockerConfig()
  }

  initWorkflow = async () => {
    try {
      const res = await SmartCallApi.getInboxWorkflow(this.id)

      if (res) {
        const payload = res.data.data
        runInAction(() => {
          this.inboxWorkflowEnabled = payload?.active ?? false
          this.workflow = payload?.workflow ?? null
          if (payload?.workflow) {
            this.smartCallFlowBuilderStore.loadFromWorkflow(payload.workflow)
          } else {
            this.smartCallFlowBuilderStore.buildDefaultWorkflow()
          }
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this._syncBlockerConfig()
      this.blockerStore.updateCache()
    }
  }

  updateInboxWorkflow = async ({ id, status }: { id: string; status: boolean }) => {
    if (status) {
      await SmartCallApi.enableWorkflow(id)
    } else {
      await SmartCallApi.disableWorkflow(id)
    }

    if (this.workflow) this.update({ ...this.workflow, enabled: status })

    const res = await SmartCallApi.updateInboxWorkflow(this.id, {
      workflow_id: id,
      active: status,
    })

    runInAction(() => {
      this.inboxWorkflowEnabled = res.data.data.active
      this._syncBlockerConfig()
    })
  }

  private _onShowDisableFlowModal = () => {
    return new Promise<void>((resolve) => {
      const modalId = nanoid()
      modalStore.addModal({
        disabledOnAllClose: true,
        id: modalId,
        type: ModalTypeList.ALERT,
        title: 'Disable call flow?',
        desc: 'By disabling it, incoming calls to this inbox will be dropped.',
        primaryAction: {
          text: 'Yes, disable',
          onAction: async () => {
            try {
              if (this.workflow)
                await this.updateInboxWorkflow({ id: this.workflow.id, status: false })
            } catch (e) {
              toastStore.add({
                type: 'error',
                title: 'Something went wrong. Please try again',
              })
              logger.error(e)
            } finally {
              modalStore.removeModal(modalId)
              resolve()
            }
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: async () => {
            modalStore.removeModal(modalId)
            resolve()
          },
        },
      })
    })
  }

  onActivateWorkFlow = async () => {
    if (!this._checkValidate('activate')) {
      return
    }

    try {
      if (!this.workflow || this.blockerStore.isExistChanges) {
        await this._createOrUpdate()
      }

      if (!this.workflow) {
        return
      }

      await this.updateInboxWorkflow({ id: this.workflow.id, status: true })

      toastStore.add({
        type: 'success',
        title: 'Call flow activated',
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        this.smartCallFlowBuilderStore.setError(e.errors)
      }
      logger.error(e)

      toastStore.add({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    }
  }

  onPublishChanges = async (props?: { withError?: true }) => {
    const withError = props?.withError

    if (!this._checkValidate('publish changes')) {
      if (withError) throw new Error('Validation failed')
      return
    }

    if (!this.workflow) {
      if (withError) throw new Error('No workflow')
      return
    }
    if (!this.params) {
      if (withError) throw new Error('No params')
      return
    }

    try {
      if (this.blockerStore.isExistChanges) {
        await this._createOrUpdate()
      }

      if (!this.inboxWorkflowEnabled) {
        await this.updateInboxWorkflow({ id: this.workflow.id, status: true })
      }

      toastStore.add({
        type: 'success',
        title: 'Changes published',
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        this.smartCallFlowBuilderStore.setError(e.errors)
      }
      logger.error(e)
      if (withError) {
        throw e
      }
    }
  }

  onSaveChanges = async (props?: { withError?: true }) => {
    const withError = props?.withError

    if (!this._checkValidate('save')) {
      if (withError) throw new Error('Validation failed')
      return
    }

    try {
      await this._createOrUpdate()

      toastStore.add({
        type: 'info',
        title: 'Changes saved',
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        this.smartCallFlowBuilderStore.setError(e.errors)
      }
      logger.error(e)
      if (withError) {
        throw e
      }
    }
  }

  private _checkValidate = (type: 'activate' | 'publish changes' | 'save') => {
    if (!this.smartCallFlowBuilderStore.checkHasCallActionSteps()) {
      toastStore.add({
        type: 'error',
        title: `Failed to ${type}`,
        desc: 'Add at least one calling action (e.g. Ring, Call forward, IVR, Call queue, Voicemail, Calling Agent, Play audio) to the workflow.',
      })
      return false
    }
    if (!this.smartCallFlowBuilderStore.validateForPublish()) {
      toastStore.add({
        type: 'error',
        title: `Failed to ${type}`,
        desc: 'Complete all required fields',
      })
      return false
    }
    return true
  }

  private _createOrUpdate = async () => {
    if (!this.params) {
      throw new Error('No params')
    }
    if (this.workflow) {
      const res = await SmartCallApi.updateWorkflow(this.workflow.id, this.params)
      this.update(res.data.payload)
    } else {
      const res = await SmartCallApi.createWorkflow(this.params)

      const { id } = res.data.payload
      await SmartCallApi.createInboxWorkflow(this.id, {
        workflow_id: id,
        active: this.inboxWorkflowEnabled,
      })

      this.update(res.data.payload)
    }
  }

  get isAutoRechargeRequired() {
    return this.smartCallFlowBuilderStore.isAutoRechargeRequired
  }
}
